import { formatDate } from '../../services/primitives/date-utils';

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const PLAIN = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `$1,495.40`, `-$85.00`. */
export const money = (value: number): string => CURRENCY.format(value);

/** `1,495.40` — the claim tables print bare amounts, as a printed remit does. */
export const plain = (value: number): string => PLAIN.format(value);

/** `+$3.42` / `−$125.00`: an effect on the payment, signed both ways so a credit is not read as a charge. */
export const signedMoney = (value: number): string =>
    value > 0 ? `+${money(value)}` : value < 0 ? `−${money(-value)}` : money(0);

/** `Sep 12, 2026`. */
export const day = (value: string | undefined): string => (value ? formatDate(value, 'MMM dd, yyyy') : '—');

/**
 * `090526` — service dates print the way a remit prints them (design 02). By hand: the shared date formatter has no
 * two-digit year and passes `yy` through as text.
 */
export const serviceDay = (value: string | undefined): string => {
    const match = /^(\d{2})(\d{2})-(\d{2})-(\d{2})/.exec(value || '');
    return match ? `${match[3]}${match[4]}${match[2]}` : value || '\u2014';
};

/** `090526` or `090526–090826`: a line spanning days keeps its end date (crosswalk §2, `fromDos`–`throughDos`). */
export const serviceDays = (from: string | undefined, through: string | undefined): string =>
    through && through !== from ? `${serviceDay(from)}\u2013${serviceDay(through)}` : serviceDay(from);

/** The payment method in words, for the expanded remit (crosswalk §1). The list shows the code itself. */
const PAYMENT_TYPES: Record<string, string> = {
    ACH: 'ACH',
    CHK: 'Check',
    FWT: 'Wire',
    BOP: 'BOP',
    NON: 'NON',
};

export const paymentTypeLabel = (type: string | undefined): string => (type ? PAYMENT_TYPES[type] || type : '—');
